<template>
  <div class="flex flex-col h-full w-full min-h-0 bg-[#fafbfc] z-10 border-t border-l border-[#1c4848]/10">
    <div class="p-6 border-b border-[#1c4848]/5 bg-white shrink-0">
      <h2 class="text-xs font-bold uppercase tracking-wider text-[#1c4848]/60 mb-4">Hadith Collection</h2>
      <button
        type="button"
        class="w-full py-2.5 bg-white border border-[#1c4848]/20 hover:border-[#1c4848] text-[#1c4848] rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-all hover:bg-[#1c4848]/5 group"
        @click="showAddHadithModal"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="group-hover:scale-110 transition-transform"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"></path><path d="M8 7h6"></path><path d="M8 11h8"></path></svg>
        New Hadith
      </button>
    </div>

    <div class="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar min-h-0">
      <div
        v-for="hadith in hadiths"
        :key="hadith.id"
        class="p-3 bg-white rounded-xl border border-[#1c4848]/5 cursor-pointer hover:border-[#1c4848]/30 transition-all group"
        @click="editHadith(hadith)"
      >
        <div class="flex justify-between items-start mb-2">
          <span class="text-[10px] font-bold text-[#1c4848]/40 uppercase">{{ hadithSource(hadith) }}</span>
          <span class="text-[10px] text-emerald-600 font-medium bg-emerald-50 px-1.5 py-0.5 rounded">{{ hadithGrade(hadith) }}</span>
        </div>
        <p class="font-serif text-sm text-[#1c4848] leading-relaxed line-clamp-2 mb-2">
          {{ hadithText(hadith) }}
        </p>
        <div class="flex justify-between items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <span class="text-[10px] text-[#1c4848]/40 hover:text-[#1c4848] cursor-pointer underline" @click.stop="editHadith(hadith)">View Full Text</span>
          <div class="flex gap-1 shrink-0">
            <button type="button" class="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors" title="Publish to feed" @click.stop="publishHadith(hadith)">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
            </button>
            <button type="button" class="p-1.5 text-[#1c4848]/60 hover:bg-[#1c4848]/5 rounded-md transition-colors" title="Edit" @click.stop="editHadith(hadith)">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"></path></svg>
            </button>
            <button type="button" class="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors" title="Delete" @click.stop="deleteHadith(hadith.id)">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import AddHadithModal from '@/components/app/dashboard/modals/AddHadithModal.vue';
import { useHadithStore } from '@/store/hadiths/index.js';
import { useModal } from 'vue-final-modal';
import { useContentFeedStore } from '@/store/content-feed/index.js';
import { getContentFeedType, getContentLanguageId } from '@/utils/contentFeedType.js';

const hadithStore = useHadithStore();
const contentFeedStore = useContentFeedStore();

onMounted(() => {
  hadithStore.fetchHadiths();
});

const hadiths = computed(() => hadithStore.hadiths);

function hadithSource(hadith) {
  return hadith.collection ?? hadith.source ?? hadith.book_name ?? hadith.narrator?.name ?? '—';
}

function hadithGrade(hadith) {
  return hadith.grade ?? hadith.authenticity ?? 'Sahih';
}

function hadithText(hadith) {
  const t = hadith.text ?? hadith.content ?? hadith.arabic ?? '';
  if (!t || typeof t !== 'string') return '—';
  return t.length > 150 ? t.slice(0, 150) + '…' : t;
}

function showAddHadithModal() {
  const { open, close } = useModal({
    component: AddHadithModal,
    attrs: {
      data: {},
      onConfirm() {
        hadithStore.fetchHadiths();
        close();
      }
    }
  });
  open();
}

function editHadith(hadith) {
  alert(`Edit functionality for "${hadith.title ?? 'hadith'}" is not implemented yet.`);
}

function deleteHadith(hadithId) {
  if (confirm('Are you sure you want to delete this hadith?')) {
    hadithStore.deleteHadith(hadithId)
      .then(() => hadithStore.fetchHadiths())
      .catch((err) => {
        console.error(err);
        alert('Failed to delete the hadith. Please try again.');
      });
  }
}

function publishHadith(hadith) {
  if (!confirm('Are you sure you want to publish this hadith?')) return;
  const hadithId = typeof hadith === 'object' ? hadith?.id : hadith;
  const title = typeof hadith === 'object' && hadith ? (hadith?.title ?? hadith?.text ?? 'Novi hadith') : 'Novi hadith';
  const type = getContentFeedType(hadith ?? {}, 'hadith');
  const languageId = typeof hadith === 'object' && hadith ? getContentLanguageId(hadith) : null;
  contentFeedStore.createContentFeed({ title, type, language_id: languageId }).then((response) => {
    hadithStore.attachContentFeedToHadith(hadithId, response.id);
  });
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(28, 72, 72, 0.15);
  border-radius: 3px;
}
</style>
